import { Suspense, useCallback, useState } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, useSnackbar } from '@lyearn/ui';
import Loader from '@/components/Loader';
import { Dialog } from '@/components/Modals';
import useNavigation from '@/hooks/useNavigation';
import useResponse from '@/hooks/useResponse';
import { useDuplicateActionItemMutation } from '@/modules/tasks/features/actionItems/graphql/mutations/generated/duplicateActionItem';
import routes from '@/pages/corporate/routes';

import { DialogProps } from '../types';
import Editor from './components/Editor';
import { DuplicateTaskEditorFormValues, getInitialValues } from './components/Editor/config';

export const dialogSize = css`
  max-width: 60.3rem;
  height: 80rem;
`;

function DuplicateTask({ onClose, duplicateFromId, title }: DialogProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formikSubmit, setFormikSubmit] = useState<any>();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const [, duplicateActionItem] = useDuplicateActionItemMutation();
  const navigation = useNavigation();

  const onSubmit = useCallback(
    async (formValues: DuplicateTaskEditorFormValues) => {
      setIsSubmitting(true);
      const { duplicateFromId, title, propertiesToDuplicate, fileId } = formValues;
      const response = await duplicateActionItem({
        data: {
          duplicateFromId,
          fileId,
          title,
          propertiesToDuplicate,
        },
      });
      setIsSubmitting(false);
      const newTask = response.data?.duplicateActionItem?.actionItem;
      handleResponse({
        condition: Boolean(response.data?.duplicateActionItem?.success),
        onSuccess: () => {
          showSnackbar({
            message: t('Task:DuplicateTask.SuccessMessage'),
          });
          if (newTask?.id) {
            navigation.push(routes.TasksIssue(newTask.id, newTask));
          }
          onClose();
        },
        onError: () => {
          showErrorSnackbar({
            message: t('Task:DuplicateTask.ErrorMessage'),
          });
        },
      });
    },
    [duplicateActionItem, handleResponse, onClose, showSnackbar, showErrorSnackbar, navigation, t],
  );

  return (
    <Dialog
      open
      action={{
        disabled: isSubmitting,
        isSubmitting: isSubmitting,
        text: t('Common:Actions.Duplicate'),
        onSubmit: formikSubmit,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: t('Task:DuplicateTask.Title'),
        description: t('Task:DuplicateTask.Description'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Box className="px-24 pt-8 pb-24">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <Editor
            initialValues={getInitialValues({ duplicateFromId, title, t })}
            setFormikSubmit={setFormikSubmit}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Box>
    </Dialog>
  );
}

export default DuplicateTask;
